import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { useTranslation } from "@coworker/locales";
import { ModalBody } from "@ingka/modal";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { useState } from "react";
import { FixaChoice } from "@coworker/apprestructured/src/shared/wrappers/FixaChoice/FixaChoice";
import { FixaChoiceItem } from "@coworker/apprestructured/src/shared/wrappers/FixaChoice/FixaChoiceItem";
import styles from "./TestbuyVariantSearch.module.css";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { ProductVariant } from "@coworker/apprestructured/src/shared/hooks/item/useItemVariants";

interface TestbuyVariantSearchProps {
  setIsSearching: (isSearching: boolean) => void;
  isSearching: boolean;
  productVariants: ProductVariant[];
  onClose?: () => void;
  onApply: (itemNo: string) => void;
  isApplying?: boolean;
}

const capitalizeText = (text: string) => {
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
};

const getUniqueVariants = (
  variants: ProductVariant[],
  property: "color" | "size"
) => {
  return [
    ...new Map(
      variants.map((variant) => [variant[property], variant])
    ).values(),
  ];
};

export const TestbuyVariantSearch = ({
  isSearching,
  setIsSearching,
  productVariants,
  onClose,
  onApply,
  isApplying = false,
}: TestbuyVariantSearchProps) => {
  const { t } = useTranslation();
  const handleClose = () => {
    setIsSearching(false);
    onClose?.();
  };

  const colorVariants = getUniqueVariants(productVariants, "color");
  const [firstVariant] = productVariants;
  const [selectedColor, setSelectedColor] = useState(firstVariant?.color ?? "");
  const [selectedSize, setSelectedSize] = useState(firstVariant?.size ?? "");
  const sizeVariants = getUniqueVariants(
    productVariants.filter(({ color }) => color === selectedColor),
    "size"
  ).filter(({ size }) => !!size);
  const selectedVariant = productVariants.find(
    ({ color, size }) =>
      color === selectedColor &&
      (sizeVariants.length === 0 ? !size : size === selectedSize)
  );

  const handleColorSelect = (color: string) => {
    const [firstSizeVariant] = getUniqueVariants(
      productVariants.filter((variant) => variant.color === color),
      "size"
    );

    setSelectedColor(color);
    setSelectedSize(firstSizeVariant?.size ?? "");
  };

  return (
    <FixaModal visible={isSearching} handleCloseBtn={handleClose}>
      <FixaSheets
        labelledById="testbuy-variant-search-modal-header"
        header={
          <FixaModalHeader
            id="testbuy-variant-search-modal-header"
            closeBtnClick={handleClose}
          />
        }
        footer={
          <FixaModalFooter renderBorder>
            <FixaButton
              type="primary"
              onClick={() => selectedVariant && onApply(selectedVariant.no)}
              disabled={!selectedVariant || isApplying}
              loading={isApplying}
            >
              {t("applyString")}
            </FixaButton>
          </FixaModalFooter>
        }
        fullSize={false}
        size="small"
      >
        <ModalBody>
          <FixaText className={styles["selectLabel"]}>
            <strong>{t("chooseColorString")}:</strong>{" "}
            {capitalizeText(selectedColor)}
          </FixaText>
          {colorVariants.length > 0 && (
            <FixaChoice
              className={styles["colorChoice"]}
              preselectedIndex={0}
              subtle
            >
              {colorVariants.map(({ color, smallImageUrl }) => (
                <FixaChoiceItem
                  key={color}
                  id={color}
                  title=""
                  image={{ src: smallImageUrl, alt: color }}
                  onClick={() => handleColorSelect(color)}
                />
              ))}
            </FixaChoice>
          )}
          {sizeVariants.length > 0 && (
            <>
              <FixaText className={styles["selectLabel"]}>
                <strong>{t("chooseSizeString")}:</strong> {selectedSize}
              </FixaText>
              <FixaChoice key={selectedColor} preselectedIndex={0}>
                {sizeVariants.map(({ size, smallImageUrl }) => (
                  <FixaChoiceItem
                    key={size}
                    id={size}
                    title={size}
                    image={{ src: smallImageUrl, alt: size }}
                    onClick={() => setSelectedSize(size)}
                  />
                ))}
              </FixaChoice>
            </>
          )}
        </ModalBody>
      </FixaSheets>
    </FixaModal>
  );
};
